import React from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import Layout from '../components/Layout';
import SEO from '../components/Seo';

type Course = {
  id: string;
  name: string;
  description: {
    description: string;
  };
  body: {
    body?: string;
  };
  featuredImage?: {
    fluid: {
      src: string;
    };
  };
  tags: string;
  pageLink?: string;
};

const isExternalLink = (url: string): boolean =>
  url.startsWith('http://') || url.startsWith('https://');

// Helper function to extract plain text from Contentful rich text or use description
const getPreviewText = (course: Course, maxLength: number = 150): string => {
  const descText = course.description?.description?.trim();
  if (descText) {
    const text = descText;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
  const richText = course.body?.body;
  if (!richText) return '';
  let text = '';
  const parsed =
    typeof richText === 'string'
      ? (() => {
          try {
            return JSON.parse(richText) as { content?: any[] };
          } catch {
            return {};
          }
        })()
      : (richText as { content?: any[] });
  if (parsed?.content && Array.isArray(parsed.content)) {
    const extractText = (node: any): string => {
      if (node.nodeType === 'text') return node.value || '';
      if (node.content && Array.isArray(node.content)) {
        return node.content.map(extractText).join('');
      }
      return '';
    };
    text = parsed.content.map(extractText).join(' ').trim();
  }
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const AsynchronousCourses: React.FC = () => {
  const data = useStaticQuery(graphql`
    {
      allContentfulCourse(
        filter: { node_locale: { eq: "en-US" } }
        sort: { fields: name, order: ASC }
      ) {
        edges {
          node {
            id
            name
            description {
              description
            }
            body {
              body
            }
            featuredImage {
              fluid {
                src
              }
            }
            tags
            pageLink
          }
        }
      }
    }
  `);

  const courses: Course[] = data.allContentfulCourse.edges.map(
    (e: any) => e.node
  );

  return (
    <Layout>
      <SEO title="Asynchronous Courses" />
      <div className="min-h-screen bg-background overflow-hidden sans-serif">
        <section className="py-20 magazine-bg">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto">
              {/* Section Title */}
              <div className="text-center mb-12">
                <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
                  Asynchronous Courses
                </h1>
                <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
              </div>

              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {courses.map((course: Course) => {
                  const previewText = getPreviewText(course);
                  const tagList = course.tags
                    ? course.tags.split(',').map((t) => t.trim()).filter(Boolean)
                    : [];
                  const cardClassName =
                    'magazine-card bg-white creative-shadow overflow-hidden block transition-all hover:scale-105';
                  const cardContent = (
                    <>
                      {/* Image */}
                      {course.featuredImage?.fluid?.src && (
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={course.featuredImage.fluid.src}
                            alt={course.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {/* Content */}
                      <div className="p-6">
                        {tagList.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {tagList.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center rounded-full px-3 py-1 text-sm sans-serif bg-blue-200 text-primary"
                              >
                                <span className="mr-2 opacity-60">
                                  <FontAwesomeIcon icon={faTag} />
                                </span>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <h3 className="text-xl font-bold text-primary mb-3 leading-tight">
                          {course.name}
                        </h3>
                        {previewText && (
                          <p className="text-foreground/70 text-sm leading-relaxed line-clamp-3">
                            {previewText}
                          </p>
                        )}
                        {course.pageLink && (
                          <div className="text-primary font-semibold text-sm mt-4">
                            Learn More →
                          </div>
                        )}
                      </div>
                    </>
                  );
                  return course.pageLink ? (
                    isExternalLink(course.pageLink) ? (
                      <a
                        key={course.id}
                        href={course.pageLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cardClassName}
                      >
                        {cardContent}
                      </a>
                    ) : (
                      <Link
                        key={course.id}
                        to={course.pageLink}
                        className={cardClassName}
                      >
                        {cardContent}
                      </Link>
                    )
                  ) : (
                    <div key={course.id} className={cardClassName}>
                      {cardContent}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AsynchronousCourses;
